import { Link } from '../../../cdl/Link/Link';
import { Text } from '../../../cdl/Text/Text';
import { BooleanStatus } from '../../../common/BooleanStatus/BooleanStatus';
import { DataSheet } from '../../../common/DataSheet/DataSheet';
import { createdBy } from '../../../common/helpers/createdBy.helper';
import { formatCompanyName } from '../../../common/helpers/formatCompanyName.helper';
import { formatDateDelivery } from '../../../common/helpers/formatDateDelivery.helper';
import { formatVessel } from '../../../common/helpers/formatVessel.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { Characters } from '../../../common/helpers/unicode.helper';
import { HistoricBuyerRefPopover } from '../../../common/HistoricBuyerRefPopover/HistoricBuyerRefPopover';
import { useRole } from '../../../common/hooks/useRole';
import { InternalNamePopover } from '../../../common/InternalNamePopover';
import { MultilineParagraph } from '../../../common/MultilineParagraph/MultilineParagraph';
import { Box } from '../../../common/ui/Box';
import { Port } from '../../../port/Port';
import { OrderModel } from '../../model/OrderModel';

interface OrderDetailsProps {
    order: OrderModel;
}

export const OrderDetails = ({ order }: OrderDetailsProps) => {
    const role = useRole();

    return (
        <DataSheet>
            <DataSheet.Label>{translate('order.detail.coreData.vessel')}</DataSheet.Label>
            <Text aria-label="Vessel name">
                <Link to="/vessel/$id" params={{ id: order.vesselId }} size="small">
                    {formatVessel({ vessel: order.vessel })}
                </Link>
            </Text>

            {order.dateDelivery ? (
                <>
                    <DataSheet.Label>{translate('order.detail.coreData.dateDelivery')}</DataSheet.Label>
                    <Text>{formatDateDelivery({ date: order.dateDelivery })}</Text>
                </>
            ) : null}

            {order.port ? (
                <>
                    <DataSheet.Label>{translate('order.detail.coreData.port')}</DataSheet.Label>
                    <Port port={order.port} />
                </>
            ) : null}

            {order.agentInformation?.name ? (
                <>
                    <DataSheet.Label>{translate('order.detail.coreData.agent')}</DataSheet.Label>
                    <Text>{order.agentInformation.name}</Text>
                </>
            ) : null}

            {order.buyerReference || order.historicBuyerReferences?.length ? (
                <>
                    <DataSheet.Label>{translate('order.detail.coreData.buyerReference')}</DataSheet.Label>
                    <Box display="inline-flex" alignItems="center" gap={4}>
                        <Text>{order.buyerReference || Characters.EN_DASH}</Text>
                        {order.historicBuyerReferences?.length ? (
                            <HistoricBuyerRefPopover references={order.historicBuyerReferences} />
                        ) : null}
                    </Box>
                </>
            ) : null}

            {!role.isOneCompanyUser() ? (
                <>
                    <DataSheet.Label>{translate('order.detail.coreData.company')}</DataSheet.Label>
                    <Box display="flex" alignItems="center" gap={8}>
                        <Text>{formatCompanyName({ company: order.customer })}</Text>
                        <InternalNamePopover company={order.customer!} />
                    </Box>
                </>
            ) : null}

            <DataSheet.Label>{translate('order.detail.coreData.dateCreated')}</DataSheet.Label>
            <Text>{createdBy({ order })}</Text>

            <DataSheet.Label>{translate('order.detail.coreData.spotSuppliers')}</DataSheet.Label>
            <Text>
                <BooleanStatus value={!!order.spot} />
            </Text>

            {role.isSuperAdmin() && order.comment ? (
                <>
                    <DataSheet.Label>{translate('order.detail.coreData.comment')}</DataSheet.Label>
                    <MultilineParagraph>{order.comment}</MultilineParagraph>
                </>
            ) : null}

            {order.creationComment ? (
                <>
                    <DataSheet.Label>{translate('order.detail.coreData.creationComment')}</DataSheet.Label>
                    <MultilineParagraph>{order.creationComment}</MultilineParagraph>
                </>
            ) : null}
        </DataSheet>
    );
};
